import { useState, useCallback, useEffect, useRef } from 'react';
import * as React from 'react';
import './loginScreen.css';
import { addLog } from '../../store/auth-store';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import TokenService from '../../services/token.service';
import { BASE_API_URL, LOGIN } from '../../services/config';
import Cookies from 'js-cookie';
import { useForm } from 'react-hook-form';
import bgImage from './bg1.svg';
import { ReactComponent as Txtimg } from './farmlogicstxt.svg';
import { ReactComponent as Pow } from './pow.svg';
import SVGIcon from '../../components/DynamicIcon';
import authAxios from '../../services/Api';
import { usePermissions } from '../../shared/Permissions/PermissionsContext';
import debounce from 'lodash.debounce';
import { setTheme } from '../../redux/themeSlice';
import i18n from '../../i18n';

function LoginScreen() {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const navigateTo = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { setPermissions } = usePermissions();

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  // Fetches role permissions and stores them encrypted in a cookie
  const fetchAndSetPermissions = useCallback(async (role) => {
    if (!role) return;
    try {
      const response = await authAxios.get(
        `/api/v1/permission/permissionByRole/${role}`
      );
      if (response.status === 200) {
        const fetchedMenuItems = response.data?.data[0]?.showPermission;
        Cookies.set('permissions', TokenService.encryptData(fetchedMenuItems));
        setPermissions(fetchedMenuItems);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ?? 'An unexpected error occurred.',
        { autoClose: 5000 }
      );
    }
  }, [setPermissions]);

  // Plain async login handler — all dependencies fully visible to React linter.
  // Separated from debounce so useCallback works correctly with no warnings.
  const handleLogin = useCallback(async (data) => {
    setIsLoading(true);
    const { username, password } = data;

    try {
      const response = await axios.post(
        `${BASE_API_URL}${LOGIN}`,
        { user_name: username.trim(), password },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.status === 201) {
        const {
          user_id,
          role,
          roleName,
          org_id,
          org_name,
          logo,
          theme,
          disabledashboard,
          user_name: userName,
          language: userLang,
          mustResetPassword,
        } = response.data;

        // Fetch and store CSRF token immediately after login
        const csrf = await authAxios.get('/api/v1/csrf-token');
        TokenService.setCsrfToken(csrf.data.csrfToken);

        // Encrypt and store all identity cookies via TokenService
        const cookieOptions = {
          secure: process.env.NODE_ENV !== 'development',
          sameSite: 'strict',
          path: '/',
        };
        Cookies.set('userdetails', TokenService.encryptData(userName), cookieOptions);
        Cookies.set('user_id', TokenService.encryptData(user_id), cookieOptions);
        Cookies.set('org_id', TokenService.encryptData(org_id), cookieOptions);
        Cookies.set('user_role', TokenService.encryptData(role), cookieOptions);
        Cookies.set('user_role_name', TokenService.encryptData(roleName), cookieOptions);
        Cookies.set('org_name', TokenService.encryptData(org_name), cookieOptions);
        Cookies.set('org_logo', TokenService.encryptData(logo), cookieOptions);
        Cookies.set('disabledashboard', TokenService.encryptData(disabledashboard), cookieOptions);
        // Language preference (plain value — not sensitive)
        Cookies.set('userLanguage', userLang);
        i18n.changeLanguage(userLang);

        // Update Redux state
        dispatch(setTheme(theme ?? 'Default'));

        setIsLoading(false);

        // The reset-password popup is shown by App.js once the dashboard has
        // loaded — this screen unmounts as soon as loginStatus flips true, so
        // we only leave a flag behind for App.js to pick up.
        if (mustResetPassword) {
          sessionStorage.setItem('mustResetPassword', 'true');
        } else {
          sessionStorage.removeItem('mustResetPassword');
        }

        dispatch(addLog(true));

        // /dashboard now renders the Platform Analytics page for superadmin
        // (previously routed to /organizations here because /dashboard used
        // to be a dead end for that account) — so every login lands on
        // /dashboard the same way now, superadmin included.
        navigateTo('/dashboard');

        // Fetch permissions after navigation — non-blocking, no local state touched
        fetchAndSetPermissions(role);
        return;
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ?? 'An unexpected error occurred.',
        { autoClose: 5000 }
      );
      setIsLoading(false);
    }
  }, [dispatch, fetchAndSetPermissions, navigateTo]);

  // useRef holds the debounced wrapper so it is created ONCE on mount.
  // Fixes the useCallback + debounce anti-pattern which causes:
  //   ESLint: "dependencies are unknown" warning
  //   A new debounce instance on every render (breaks debouncing entirely)
  const debouncedLogin = useRef(
    debounce((data, fn) => fn(data), 300)
  ).current;

  // Cancel any pending debounced call on unmount — prevents memory leaks
  useEffect(() => {
    return () => debouncedLogin.cancel();
  }, [debouncedLogin]);

  const handleSubmitForm = handleSubmit((data) => {
    if (!isLoading) {
      debouncedLogin(data, handleLogin);
    }
  });

  return (
    <div className="page-container">
      <div
        className="image-section"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          height: '100vh',
          width: '100%',
        }}
      >
        <Txtimg className="slide-svg" />
        <div className="text-hi">Hello,</div>
        <div className="text-welcome">Welcome!</div>
        <div className="text-sub">
          Design, Build &amp; Launch <br />Effortlessly.
        </div>
        <Pow className="pow" />
      </div>

      <div className="login-form-section">
        <form className="login-form" onSubmit={handleSubmitForm}>
          <div className="login-Heading">Login</div>
          <div className="flowforms-login-input-container">

            <div className="flowforms-login-input-field">
              <div className="login-form-group">
                <i className="input-icon">
                  <SVGIcon name="username" id="username" />
                </i>
                <input
                  id="username"
                  name="username"
                  className="login-form-field"
                  placeholder="Username"
                  {...register('username', {
                    required: 'Username is required.',
                  })}
                  autoComplete="username"
                  aria-describedby="usernameError"
                />
              </div>
              {errors.username && (
                <span id="usernameError" className="error active">
                  {errors.username.message}
                </span>
              )}
            </div>

            <div className="flowforms-login-input-field">
              <div className="login-form-group">
                <i className="input-icon">
                  <SVGIcon name="password" />
                </i>
                <input
                  id="password"
                  placeholder="Password"
                  className="login-form-field"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: 'Password is required.',
                  })}
                  autoComplete="current-password"
                  aria-describedby="passwordError"
                />
                <span
                  className="password-toggle-icon"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <SVGIcon name="outlinepassword" width={17} height={13} />
                  ) : (
                    <SVGIcon name="removeoutlinepassword" width={17} height={15} />
                  )}
                </span>
              </div>
              {errors.password && (
                <span id="passwordError" className="error active">
                  {errors.password.message}
                </span>
              )}
            </div>

            <a href="/forgot_password" className="login-forgot-password">
              Forgot password?
            </a>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="form-actions-loginbotton"
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Log in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LoginScreen;